import { PageDetail } from '@moar/ui';
import { GetStaticPaths, GetStaticProps } from 'next';
import { TypeObjectDetailPageProps, TypeTimelineObject } from '@moar/types';
import { getObjectSlugsData, fetchTimeline } from '@moar/data';

const apiUrl = process.env['NEXT_PUBLIC_API_URL_TIMELINE_AMREV'] || '';
const apiUrlPreview = process.env['NEXT_PUBLIC_API_URL_TIMELINE_AMREV_PREVIEW'] || '';

export const getStaticPaths: GetStaticPaths = async () => {
    const slugs = await getObjectSlugsData({
        apiUrl,
        isProduction: process.env.NODE_ENV === 'production',
    });
    const allPaths = slugs.map((slug) => ({ params: { slug: slug } }));

    return {
        paths: allPaths,
        fallback: true,
    };
};

export const getStaticProps: GetStaticProps = async ({ params = {}, preview = false }) => {
    const slug = String(params.slug);

    const data = await fetchTimeline({
        apiUrl: preview ? apiUrlPreview : apiUrl,
        isProduction: process.env.NODE_ENV === 'production',
    });

    const objectsAndDates = data.timelineSections.data.map((section) => section.objects.map((item) => item)).flat();
    const objects = objectsAndDates.filter((item) => item.type !== 'timeline-section-date');
    const onlyObjects = objects as unknown as TypeTimelineObject[];
    const object = onlyObjects.find((obj) => obj.slug === slug);

    const objectIndex = onlyObjects.findIndex((obj) => obj.slug === slug);
    const prevObject = onlyObjects[objectIndex - 1] ? onlyObjects[objectIndex - 1] : null;
    const nextObject = onlyObjects[objectIndex + 1] ? onlyObjects[objectIndex + 1] : null;

    // find section for this object
    const objectsPerSection = data.timelineSections.data.map((section) =>
        section.objects.filter((item) => item.type !== 'timeline-section-date')
    );
    const onlyObjectsPerSection = objectsPerSection as unknown as TypeTimelineObject[][];
    const objectSlugsPerSection = onlyObjectsPerSection.map((section) => section.map((item) => item.slug));
    const sectionIndex = objectSlugsPerSection.findIndex((slugs) => slugs.includes(slug));
    const section = data.timelineSections.data[sectionIndex];

    // remove objects to keep light for cache - needed for nav
    const sections = data.timelineSections.data.map((section) => {
        const copy = { ...section };
        copy.objects = [];
        return copy;
    });

    return {
        props: {
            timelineTitle: data.title,
            page: object,
            ...(prevObject?.slug && { prevObjectSlug: prevObject.slug }),
            ...(nextObject?.slug && { nextObjectSlug: nextObject.slug }),
            sectionColor: section.color,
            sectionColorIsTint: section.colorIsTint,
            sectionPosition: section.position,
            sectionTitle: section.title,
            sections,
            preview,
            offlineAlertBanner: data.misc.offlineAlertBanner,
            aboutPageTitle: data.about.aboutPageTitle,
            howToPageTitle: data['how-to'].howToPageTitle,
            glossaryPageTitle: data.glossary.glossaryPageTitle,
            glossaryItemsCount: data.timelineGlossary.data.length,
        },
    };
};

export function ObjectDetailPage(props: TypeObjectDetailPageProps) {
    const { page } = props;

    if (!page) return null;

    return <PageDetail {...props} />;
}

export default ObjectDetailPage;
